import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useT } from "../i18n/hooks.ts";
import { groupByPeriod, GRANULARITY_PERIOD_KEY, type Granularity } from "../model/aggregates.ts";
import { isoToDdmmyyyy } from "../model/isoDate.ts";
import type { SubBasinResult } from "../engine/engine.ts";

/** A sub-basin's contribution across the loaded series, bucketed at `granularity` (CalculationResults.tsx's own selector), in the chosen units (see model/units.ts). */
export function ContributionChart({
  result,
  dates,
  area,
  coefficient,
  unit,
  granularity,
}: {
  result: SubBasinResult;
  dates: string[];
  area: number;
  coefficient: number;
  unit: string;
  granularity: Granularity;
}) {
  const t = useT();
  // Real gap found by deep-review (2026-08-29): recomputed on every render
  // (an O(dates) scan — 26,298 days for a real Duero-scale series) even
  // when none of these actually changed, e.g. a sibling panel's own state
  // update.
  const data = useMemo(
    () =>
      groupByPeriod(dates, result.contribution, granularity).map((r) => ({
        period: r.period,
        hm3: r.totalMm * area * coefficient,
      })),
    [dates, result.contribution, granularity, area, coefficient]
  );

  return (
    <div className="chart">
      <h3>
        {t("contributionChart.title", {
          title: result.description,
          unit,
          period: t(GRANULARITY_PERIOD_KEY[granularity]),
        })}
      </h3>
      <ResponsiveContainer width="100%" height={280}>
        {granularity === "daily" ? (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="period" tickFormatter={(iso: string) => isoToDdmmyyyy(iso)} />
            <YAxis />
            <Tooltip formatter={(v) => Number(v).toFixed(2)} />
            <Line
              type="monotone"
              dataKey="hm3"
              name={unit}
              stroke="#3fa7b3"
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        ) : (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={(v) => Number(v).toFixed(2)} />
            <Bar dataKey="hm3" name={unit} fill="#3fa7b3" />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
